import React from "react";
import { MdCheckCircle } from "react-icons/md";

const raleway = { fontFamily: "'Geist', sans-serif" };
const geistMono = { fontFamily: "'Geist Mono', monospace" };
const instrumentSerif = { fontFamily: "'Instrument Serif', Georgia, serif" };

const CUSTOMER_RIGHTS = [
  {
    lead: "Right to be heard.",
    body: "Every complaint is logged, acknowledged, and assigned a reference number.",
  },
  {
    lead: "Right to clear information.",
    body: "Understand charges, terms, and the status of your complaint at every stage.",
  },
  {
    lead: "Right to timely resolution.",
    body: "Receive a response within agreed timelines, or be informed if additional time is required.",
  },
  {
    lead: "Right to escalate unresolved complaints.",
    body: "If your issue remains unresolved, you have the right to escalate it to the Central Bank of Nigeria (CBN).",
  },
];

const RESOLUTION_STEPS = [
  {
    step: "01",
    title: "Submit your complaint to Zikora",
    body: "Use the form above, call, email, WhatsApp, or visit any branch. You'll receive a complaint reference number for tracking.",
    meta: "Acknowledged Within 24 Hours",
    isHighlight: false,
  },
  {
    step: "02",
    title: "Investigation and resolution",
    body: "Our team reviews your complaint, keeps you informed, and works toward a fair and timely outcome.",
    meta: "Most Cases Resolved Within 14 Days",
    isHighlight: false,
  },
  {
    step: "03",
    title: "Escalate to the CBN",
    body: "If your complaint remains unresolved after 14 days, you may escalate it to the Consumer Protection Department of the Central Bank of Nigeria.",
    meta: "Cpd@Cbn.Gov.Ng · 07002255226",
    isHighlight: true,
  },
];

const SupportPromise = () => {
  return (
    <>
      <style>{`
				.support-rights-row {
					display: flex;
					gap: clamp(28px, 4vw, 64px);
					align-items: flex-start;
				}
				.support-rights-col { flex: 1 1 0; min-width: 0; }
				@media (max-width: 900px) {
					.support-rights-row { flex-direction: column !important; gap: 40px !important; }
				}
			`}</style>

      <section
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding:
            "clamp(32px, 4vw, 60px) clamp(20px, 5.56vw, 80px) clamp(56px, 6.94vw, 100px)",
        }}
      >
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          {/* Section header */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: "clamp(12px, 1.4vw, 20px)",
              marginBottom: "clamp(40px, 5vw, 72px)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "#295B4A",
                  display: "inline-block",
                }}
              />
              <span
                style={{
                  ...geistMono,
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: 1,
                  letterSpacing: "0.1em",
                  color: "#295B4A",
                  textTransform: "uppercase",
                }}
              >
                Know your rights
              </span>
            </div>

            <h2
              style={{
                ...instrumentSerif,
                fontWeight: 400,
                fontSize: "clamp(28px, 3.9vw, 56px)",
                lineHeight: 1.15,
                letterSpacing: "-0.01em",
                color: "#132A22",
                margin: 0,
                maxWidth: "min(820px, 100%)",
              }}
            >
              Fair treatment, clear timelines, and a clear path forward.
            </h2>
          </div>

          <div className="support-rights-row">
            {/* Left — Your Rights as a Customer */}
            <div className="support-rights-col">
              <h3
                style={{
                  ...raleway,
                  fontWeight: 600,
                  fontSize: "clamp(18px, 1.4vw, 20px)",
                  color: "#132A22",
                  margin: "0 0 clamp(20px, 2vw, 32px) 0",
                }}
              >
                Your Rights as a Customer
              </h3>

              <ul
                style={{
                  margin: 0,
                  padding: 0,
                  listStyle: "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: "clamp(18px, 1.8vw, 24px)",
                }}
              >
                {CUSTOMER_RIGHTS?.map((r) => (
                  <li
                    key={r?.lead}
                    style={{
                      display: "flex",
                      gap: "12px",
                      alignItems: "flex-start",
                    }}
                  >
                    <MdCheckCircle
                      size={18}
                      style={{
                        color: "#295B4A",
                        flexShrink: 0,
                        marginTop: "2px",
                      }}
                    />
                    <p
                      style={{
                        ...raleway,
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: 1.65,
                        color: "#5E5E5E",
                        margin: 0,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 500,
                          color: "#132A22",
                        }}
                      >
                        {r?.lead}
                      </span>{" "}
                      {r?.body}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right — The Resolution Process */}
            <div className="support-rights-col">
              <h3
                style={{
                  ...raleway,
                  fontWeight: 600,
                  fontSize: "clamp(18px, 1.4vw, 20px)",
                  color: "#132A22",
                  margin: "0 0 clamp(20px, 2vw, 32px) 0",
                }}
              >
                The Resolution Process
              </h3>

              <div
                style={{
                  background: "transparent",
                  border: "1px solid #E5E9E6",
                  borderRadius: "20px",
                  padding: "clamp(24px, 2.6vw, 36px)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "clamp(22px, 2.4vw, 32px)",
                }}
              >
                {RESOLUTION_STEPS?.map((s) => (
                  <div
                    key={s?.step}
                    style={{
                      display: "flex",
                      gap: "clamp(14px, 1.5vw, 20px)",
                      alignItems: "flex-start",
                    }}
                  >
                    <div
                      style={{
                        flexShrink: 0,
                        width: "44px",
                        height: "44px",
                        borderRadius: "50%",
                        background: s?.isHighlight ? "#132A22" : "#F1F4F0",
                        color: s?.isHighlight ? "#FFFFFF" : "#132A22",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        ...geistMono,
                        fontWeight: 500,
                        fontSize: "13px",
                      }}
                    >
                      {s?.step}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <span
                        style={{
                          ...raleway,
                          fontWeight: 600,
                          fontSize: "clamp(15px, 1.2vw, 17px)",
                          color: "#132A22",
                          lineHeight: 1.3,
                        }}
                      >
                        {s?.title}
                      </span>
                      <p
                        style={{
                          ...raleway,
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: 1.65,
                          color: "#6B7B70",
                          margin: 0,
                        }}
                      >
                        {s?.body}
                      </p>
                      {s?.meta && (
                        <span
                          style={{
                            ...raleway,
                            fontWeight: 500,
                            fontSize: "13px",
                            color: "#295B4A",
                            marginTop: "2px",
                          }}
                        >
                          {s?.meta}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SupportPromise;
